import { retryAfterMs } from '../retryAfter';

/**
 * Bulk attach (KPR-187): run many uploads a few at a time, and keep going
 * through the two failures that are not answers.
 *
 * - A 429 is the upload budget (the `expensive` class, 300 a minute per
 *   person, shared with the list itself). The wait it names applies to EVERY
 *   worker, not only the one refused: the others would be refused too, and
 *   each refusal spends a request. Nothing is failed for a 429 until it has
 *   come back MAX_RATE_WAITS times for the same file.
 * - No response at all (a dropped connection, the laptop's wifi) is retried
 *   NETWORK_RETRIES times, after NETWORK_BACKOFF_MS.
 * - Anything else is keepr's answer (a lock, a refused type, a file too big):
 *   that file fails with it, and the rest carry on.
 *
 * The api governor (src/api/governor.js) never retries a POST's 429, which is
 * why this does. `sleep` and `now` are injectable so the tests run in no time.
 */

export const DEFAULT_CONCURRENCY = 3;
export const NETWORK_RETRIES = 2;
export const NETWORK_BACKOFF_MS = [1000, 3000];
export const MAX_RATE_WAITS = 5;
/** When a 429 names no wait. */
export const DEFAULT_RATE_WAIT_MS = 5000;
/** Never wait longer than this for one 429. */
export const MAX_RATE_WAIT_MS = 60_000;

const defaultSleep = (ms, signal) => new Promise((resolve) => {
	if (signal?.aborted) { resolve(); return; }
	const timer = setTimeout(resolve, ms);
	signal?.addEventListener?.('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
});

const isNetwork = err => Boolean(err) && !err.response && err.code !== 'ERR_CANCELED' && err.name !== 'CanceledError' && err.name !== 'AbortError';
const isCancel = err => err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError';

/**
 * `tasks`: `[{ key, ... }]`. `run(task, { onProgress, signal })` does one
 * attempt and resolves with its result or throws the axios error.
 * `onState(key, state)` hears `{ status: 'queued'|'running'|'waiting'|'done'|'failed'|'cancelled', progress?, result?, error? }`.
 * Resolves `{ done: [key], failed: [{ key, error }], cancelled: [key] }`.
 */
export async function runQueue(tasks, {
	run,
	onState = () => {},
	concurrency = DEFAULT_CONCURRENCY,
	signal = null,
	sleep = defaultSleep,
	now = () => Date.now(),
} = {}) {
	const queue = [...tasks];
	const outcome = { done: [], failed: [], cancelled: [] };
	let pausedUntil = 0;

	for (const t of queue) onState(t.key, { status: 'queued', progress: 0 });

	const waitForPause = async () => {
		while (!signal?.aborted && pausedUntil > now()) {
			await sleep(pausedUntil - now(), signal);
		}
	};

	const attemptTask = async (task) => {
		let networkTries = 0;
		let rateWaits = 0;
		for (;;) {
			await waitForPause();
			if (signal?.aborted) return { cancelled: true };
			onState(task.key, { status: 'running', progress: 0 });
			try {
				const result = await run(task, {
					signal,
					onProgress: (fraction) => onState(task.key, { status: 'running', progress: Math.max(0, Math.min(1, fraction)) }),
				});
				return { result };
			} catch (err) {
				if (signal?.aborted || isCancel(err)) return { cancelled: true };
				const status = err?.response?.status;
				if (status === 429 && rateWaits < MAX_RATE_WAITS) {
					rateWaits += 1;
					const wait = Math.min(retryAfterMs(err, { now: now() }) ?? DEFAULT_RATE_WAIT_MS, MAX_RATE_WAIT_MS);
					pausedUntil = Math.max(pausedUntil, now() + wait);
					onState(task.key, { status: 'waiting', progress: 0 });
					continue;
				}
				if (isNetwork(err) && networkTries < NETWORK_RETRIES) {
					const wait = NETWORK_BACKOFF_MS[networkTries] ?? NETWORK_BACKOFF_MS[NETWORK_BACKOFF_MS.length - 1];
					networkTries += 1;
					onState(task.key, { status: 'waiting', progress: 0 });
					await sleep(wait, signal);
					continue;
				}
				return { error: err };
			}
		}
	};

	const worker = async () => {
		while (queue.length) {
			const task = queue.shift();
			if (signal?.aborted) {
				outcome.cancelled.push(task.key);
				onState(task.key, { status: 'cancelled' });
				continue;
			}
			const r = await attemptTask(task);
			if (r.cancelled) {
				outcome.cancelled.push(task.key);
				onState(task.key, { status: 'cancelled' });
			} else if (r.error) {
				outcome.failed.push({ key: task.key, error: r.error });
				onState(task.key, { status: 'failed', error: r.error });
			} else {
				outcome.done.push(task.key);
				onState(task.key, { status: 'done', progress: 1, result: r.result });
			}
		}
	};

	await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, tasks.length)) }, worker));
	return outcome;
}
